import React, { useState } from 'react';
import { searchRecords } from '../api';
import AppHeader from './AppHeader';
import { isEmployee, usesSearchHome } from '../utils/roles';

function typeLabel(type) {
  if (type === 'season_pass') return 'Season Pass';
  if (type === 'order') return 'Order';
  if (type === 'case') return 'Case';
  if (type === 'submission') return 'Submission';
  return type || 'Record';
}

function Search({ user, onLogout, onNavigate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const employeeHome = isEmployee(user);
  const searchHome = usesSearchHome(user);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setSearched(true);

    try {
      const trimmedQuery = searchQuery.trim();
      if (!trimmedQuery) {
        setResults([]);
        setCounts(null);
        setLoading(false);
        return;
      }
      const data = await searchRecords(trimmedQuery);
      setResults(data.results || []);
      setCounts(data.counts || null);
    } catch (err) {
      setError(err.error || 'Search failed');
      setResults([]);
      setCounts(null);
    } finally {
      setLoading(false);
    }
  };

  const openResult = (result) => {
    if (result.type === 'season_pass') {
      onNavigate('season-pass-detail', {
        seasonPassId: result.recordId || result.id,
        returnTo: 'search',
      });
      return;
    }
    if (result.type === 'order') {
      onNavigate('orders', { orderId: result.orderId || result.id });
      return;
    }
    if (result.type === 'case') {
      onNavigate('cases', { caseId: result.caseId || result.id });
      return;
    }
    if (result.type === 'submission' && !employeeHome) {
      onNavigate('submit', {
        prefill: {
          guestName: result.guestName,
          guestEmail: result.guestEmail,
          ticketType: result.ticketType,
          ticketId: result.ticketId,
        },
      });
    }
  };

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle={searchHome ? 'Home' : 'Search Records'}
        subtitle={
          searchHome
            ? 'Search season passes, orders, cases, and guest submissions'
            : undefined
        }
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="search"
      />

      {error && <div className="error">{error}</div>}

      <p className="subtitle" style={{ marginBottom: '16px' }}>
        Search SQLite by season pass ID, order number, case number, guest email, or guest name.
      </p>

      <form onSubmit={handleSearch}>
        <div className="search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SFSP-88421, MA-10000001, CASE-327712, email, or name..."
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {searched && (
        <div className="search-results">
          <h2>
            Results for &quot;{searchQuery}&quot; ({results.length} found)
          </h2>
          {counts && (
            <p className="subtitle">
              Season passes {counts.seasonPasses || 0} · Orders {counts.orders || 0} · Cases{' '}
              {counts.cases || 0} · Submissions {counts.submissions || 0}
            </p>
          )}

          {results.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Email</th>
                  <th>ID / Detail</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result) => (
                  <tr key={`${result.type}-${result.id}`}>
                    <td>{typeLabel(result.type)}</td>
                    <td>{result.title}</td>
                    <td>{result.subtitle || '—'}</td>
                    <td>{result.detail || '—'}</td>
                    <td>{result.status || '—'}</td>
                    <td>
                      {(result.type === 'season_pass' ||
                        result.type === 'order' ||
                        result.type === 'case' ||
                        (result.type === 'submission' && !employeeHome)) && (
                        <button
                          type="button"
                          className="btn-table"
                          onClick={() => openResult(result)}
                        >
                          {result.type === 'submission' ? 'Add Record for Guest' : 'Open'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-results">No records found matching your search.</div>
          )}
        </div>
      )}

      {!searched && (
        <div className="no-results">
          Enter a season pass ID, order number, case number, email, or name.
        </div>
      )}
    </div>
  );
}

export default Search;
